import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import theme from '../../../styles/theme';
import useForm from '../../../hooks/useForm';
import { userAPI } from '../../../service/api';

import { Header, Spacer, TextButton, Text } from '../../atoms';
import { TextField } from '../../molecules';
import Modal from '../Modal';

const SignUpWrapper = styled.form`
  display: flex;
  flex-direction: column;
  margin: auto;
  align-items: center;
  padding: ${theme.spacing.l} 130px;
  box-shadow: ${theme.surface.boxShadow};
  border-radius: ${theme.surface.borderRadius};

  ${theme.mediaQuery('tablet')} {
    padding: 0;
    box-shadow: none;
  }
`;

const SignUpForm = () => {
  const history = useHistory();
  const [modalVisible, setModalVisible] = useState(false);

  const openModal = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    if (modalVisible) {
      setModalVisible(false);
      history.push('/login');
    }
  };

  const handleSignUp = useCallback(async (data) => {
    const response = await userAPI.signUp(data);
    return response;
  }, []);

  const { errors, isLoading, handleChange, handleSubmit } = useForm({
    initialValue: {
      email: '',
      password: '',
      checkPassword: '',
      fullName: '',
    },
    onSubmit: async (values) => {
      const signUp = await handleSignUp({
        email: values.email,
        password: values.password,
        fullName: values.fullName,
        username: values.fullName,
      });

      if (signUp) {
        openModal();
      }
    },
    validate: ({ email, password, checkPassword, fullName }) => {
      const errors = {};

      if (!email) {
        errors.email = '아이디를 입력해주세요.';
      } else if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]+$/.test(email)) {
        errors.email = '올바른 이메일을 입력해주세요.';
      }
      if (!password) {
        errors.password = '비밀번호를 입력해주세요.';
      } else if (!/^[A-Za-z0-9+]{8,12}$/.test(password)) {
        errors.password =
          '비밀번호 형식(8~12자리 영문 + 숫자 조합)을 확인해주세요.';
      }

      if (!checkPassword) {
        errors.checkPassword = '비밀번호를 한 번 더 입력해주세요.';
      }
      if (password !== checkPassword) {
        errors.checkPassword = '입력한 비밀번호와 달라요.';
      }

      if (!fullName) {
        errors.fullName = '닉네임을 입력해주세요.';
      } else if (!/^[가-힣]+$/.test(fullName)) {
        errors.fullName = '닉네임은 한글로 입력해주세요.';
      }

      return errors;
    },
  });

  return (
    <SignUpWrapper onSubmit={handleSubmit}>
      <Header style={{ marginBottom: theme.spacing.l }}>회원가입</Header>

      <Spacer align="vertical" size={theme.spacing.xs}>
        <TextField
          name="email"
          type="text"
          isFullWidth
          placeholder="이메일 형식으로 입력해주세요."
          label="아이디"
          iconName="at-sign"
          onChange={handleChange}
          error={!!errors.email}
        />
        {errors.email ? (
          <Text
            size="xs"
            color={theme.colors.error}
            style={{ textAlign: 'right' }}
          >
            {errors.email}
          </Text>
        ) : null}
        <TextField
          name="password"
          type="password"
          isFullWidth
          placeholder="비밀번호를 입력해주세요."
          label="비밀번호"
          iconName="lock"
          onChange={handleChange}
          error={!!errors.password}
        />

        {errors.password ? (
          <Text
            size="xs"
            color={theme.colors.error}
            style={{ textAlign: 'right' }}
          >
            {errors.password}
          </Text>
        ) : (
          <Text
            size="xs"
            color={theme.colors.placeholder}
            style={{ textAlign: 'right' }}
          >
            비밀번호는 8~12자리 영문 + 숫자 조합으로만 입력해주세요.
          </Text>
        )}
        <TextField
          name="checkPassword"
          type="password"
          isFullWidth
          placeholder="비밀번호를 다시 입력해주세요."
          label="비밀번호 확인"
          iconName="lock"
          onChange={handleChange}
          error={!!errors.checkPassword}
        />
        {errors.checkPassword ? (
          <Text
            size="xs"
            color={theme.colors.error}
            style={{ textAlign: 'right' }}
          >
            {errors.checkPassword}
          </Text>
        ) : null}
        <TextField
          name="fullName"
          isFullWidth
          placeholder="닉네임을 입력해주세요."
          label="닉네임"
          iconName="user"
          onChange={handleChange}
          error={!!errors.fullName}
        />
        {errors.fullName ? (
          <Text
            size="xs"
            color={theme.colors.error}
            style={{ textAlign: 'right' }}
          >
            {errors.fullName}
          </Text>
        ) : null}
        {errors.badRequest ? (
          <Text
            color={theme.colors.error}
            style={{ textAlign: 'center', marginTop: theme.spacing.m }}
          >
            이미 가입되어 있는 이메일입니다.
          </Text>
        ) : null}
        <TextButton
          type="submit"
          size="100%"
          disabled={isLoading}
          style={{ marginTop: theme.spacing.l }}
        >
          {isLoading ? '로딩중...' : '회원가입'}
        </TextButton>
      </Spacer>
      <Modal visible={modalVisible} onClose={closeModal}>
        회원가입 완료!
      </Modal>
    </SignUpWrapper>
  );
};

export default SignUpForm;
